// createClickWithTouchBackfill.js

const CLICK_FALLBACK_DELAY = 100;
const CLICK_DUPLICATE_WINDOW = 500;
const TOUCH_MOVE_THRESHOLD = 10;

const NULL_FUNCTION = () => null;
const getTouchPoint = event => event.touches?.[0] || event.changedTouches?.[0];

/**
 * Factory that returns an independent click-with-touch-backfill instance.
 * Each call creates its own closure with isolated timer / state, so
 * multiple buttons never interfere with each other.
 *
 * @returns {{ bindClick: function, cleanup: function }}
 * @example
 * const { bindClick, cleanup } = createClickWithTouchBackfill();
 * const props = bindClick({ onClick: handler });
 * // later: cleanup() to clear pending timer
 */
const createClickWithTouchBackfill = () => {
  let touchStart = null;
  let fallbackTimer = null;
  let lastNativeClickAt = 0;
  let lastTouchFallback = { element: null, triggeredAt: 0 };

  const clearFallbackTimer = () => {
    if (fallbackTimer === null) return;
    clearTimeout(fallbackTimer);
    fallbackTimer = null;
  };

  const cancelTouchFallback = () => {
    touchStart = null;
    clearFallbackTimer();
  };

  const bindClick = ({ onClick = NULL_FUNCTION } = {}) => {
    const handleClick = event => {
      const nativeClickAt = Date.now();
      lastNativeClickAt = nativeClickAt;
      clearFallbackTimer();

      if (
        lastTouchFallback.element === event.currentTarget &&
        nativeClickAt - lastTouchFallback.triggeredAt < CLICK_DUPLICATE_WINDOW
      ) {
        return;
      }

      onClick(event);
    };

    const handleTouchStart = event => {
      if (event.touches?.length !== 1) {
        cancelTouchFallback();
        return;
      }

      const touchPoint = getTouchPoint(event);
      if (!touchPoint) {
        cancelTouchFallback();
        return;
      }

      touchStart = {
        element: event.currentTarget,
        onClick,
        x: touchPoint.clientX,
        y: touchPoint.clientY,
      };
    };

    const handleTouchMove = event => {
      if (!touchStart) return;

      const touchPoint = getTouchPoint(event);
      if (!touchPoint) {
        cancelTouchFallback();
        return;
      }

      const deltaX = Math.abs(touchPoint.clientX - touchStart.x);
      const deltaY = Math.abs(touchPoint.clientY - touchStart.y);

      if (deltaX > TOUCH_MOVE_THRESHOLD || deltaY > TOUCH_MOVE_THRESHOLD) {
        cancelTouchFallback();
      }
    };

    const handleTouchEnd = event => {
      if (!touchStart) return;

      const currentTouchStart = touchStart;
      const touchEndAt = Date.now();
      const touchPoint = getTouchPoint(event);
      touchStart = null;

      if (
        touchPoint &&
        (Math.abs(touchPoint.clientX - currentTouchStart.x) >
          TOUCH_MOVE_THRESHOLD ||
          Math.abs(touchPoint.clientY - currentTouchStart.y) >
            TOUCH_MOVE_THRESHOLD)
      ) {
        clearFallbackTimer();
        return;
      }

      clearFallbackTimer();
      fallbackTimer = setTimeout(() => {
        fallbackTimer = null;

        if (lastNativeClickAt >= touchEndAt) return;

        lastTouchFallback = {
          element: currentTouchStart.element,
          triggeredAt: Date.now(),
        };
        currentTouchStart.onClick(event);
      }, CLICK_FALLBACK_DELAY);
    };

    return {
      onClick: handleClick,
      onTouchCancel: cancelTouchFallback,
      onTouchEnd: handleTouchEnd,
      onTouchMove: handleTouchMove,
      onTouchStart: handleTouchStart,
    };
  };

  return { bindClick, cleanup: clearFallbackTimer };
};

export default createClickWithTouchBackfill;
